import { useNavigate, useParams } from 'react-router';

import { Stack } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import type { EmptyStateButtons } from '@repo/common/components/NewEmptyState/EmptyState';
import { EmptyState } from '@repo/common/components/NewEmptyState/EmptyState';
import { EntityType } from '@repo/common/enums/entityType';
import { useCallback, useEffect, useMemo } from 'react';
import type { IGlossaryTerm } from '../../api';
import { useAuthUser } from '../../api';
import type {
	FetchModelInfiniteListHook,
	FetchModelList,
} from '../../api/factories/types';
import {
	fetchCatalogList,
	useCatalogInfiniteList,
} from '../../api/hooks/resourceCatalog';
import { useTeamEmptyState } from '../../api/hooks/team/emptyStates';
import { useTemplateList } from '../../api/hooks/templates';
import { entityModalStore } from '../../components/EntityModal/store';
import { useCatalogFilter } from '../../components/Filter/useCatalogFilter';
import { TableV2 } from '../../components/TableV2';
import type { OnCellClickHandlerParams } from '../../components/TableV2/types';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import { useHomepageTeam } from '../HomePage/hooks/useHomepageTeam';
import { useStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import { useActions, useColumns } from './TeamGlossaryTermsPage.hooks';

const COLUMN_VISIBILITY = {
	catalogType: EntityType.glossary,
	catalogServerType: EntityType.glossary,
} as const;

const DEFAULT_CATALOG_FILTERS = {
	operands: [
		{
			operator: 'not',
			field: 'parent_id',
			operands: [],
		},
	],
} as { readonly operands: readonly Filter[] };

const DEFAULT_SEARCH_PARAMS_NESTING = {
	entity_type: EntityType.glossary,
	calculate_children_count: true,
} as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const QUICK_ACTIONS = [
	'actions::sidebar',
	'actions::ai',
	'actions::verified',
	'actions::delete',
] as const;

const DEFAULT_SORT = {
	field: 'updated_at',
	order: 'desc',
} as const;

const WITH_CUSTOM_PROPERTIES = [EntityType.glossary];

export default function TeamGlossaryTermsPage() {
	const { teamId } = useParams();
	const { user, isAdminUser, isViewerOrGuestUser } = useAuthUser();
	const { isTeamEditorUser } = useHomepageTeam();

	const { classes } = useStyles();

	const columns = useColumns();
	const actions = useActions();

	const navigate = useNavigate();

	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	const { data: defaultTemplates } = useTemplateList({
		filters: {
			is_default: true,
			entity_type_template: EntityType.glossary,
		},
	});

	useEffect(() => {
		// If the user opens a entity page from this context we want the team
		// breadcrumb to match the team of this page.
		if (teamId) {
			setTeamBreadcrumb(teamId);
		}
	}, [setTeamBreadcrumb, teamId]);

	const handleCreateGlossaryTermClick = useCallback(async () => {
		await entityModalStore.quickCreateEntity(
			EntityType.glossary,
			'Untitled',
			[teamId ?? ''],
			{
				owners: [user.id],
			},
			navigate,
			defaultTemplates?.results?.[0]
		);
	}, [teamId, user.id, navigate, defaultTemplates]);

	const emptyState = useTeamEmptyState(teamId as string, 'glossary');

	const handleCellClick = useCallback(
		({ column, record }: OnCellClickHandlerParams<IGlossaryTerm>) => {
			if (column.navigate !== false) {
				navigate(`/glossary/${record.id}`);
			}
		},
		[navigate]
	);

	const defaultSearchParams = useMemo(
		() => ({
			team_id: teamId,
			entity_type: EntityType.glossary,
			calculate_children_count: true,
		}),
		[teamId]
	);

	const buttons: EmptyStateButtons = useMemo(
		() => [
			{
				label: 'Learn more',
				variant: 'default',
				onClick: () => {
					window.open('https://docs.secoda.com/features/glossary', '_blank');
				},
			},
			{
				label: 'Create glossary term',
				variant: 'primary',
				onClick: handleCreateGlossaryTermClick,
				requiresUseButtonPermission: true,
			},
		],
		[handleCreateGlossaryTermClick]
	);

	const filterOptions = useCatalogFilter();

	if (emptyState?.is_empty) {
		return (
			<EmptyState
				heading="Welcome to Glossary"
				body="Keep a consistent set of shared data terms across your team with the glossary."
				illustrationName="dictionary"
				buttons={buttons as EmptyStateButtons}
				hasUseButtonPermission={isTeamEditorUser || isAdminUser}
			/>
		);
	}

	return (
		<Stack className={classes.wrapper}>
			<TableV2<IGlossaryTerm>
				withCustomProperties={WITH_CUSTOM_PROPERTIES}
				pluralTypeString="terms"
				withCsvExport
				withServerCsvExport
				withInfiniteScroll
				withQuickActions={QUICK_ACTIONS}
				usePaginationList={
					useCatalogInfiniteList as FetchModelInfiniteListHook<IGlossaryTerm>
				}
				fetchPaginationList={
					fetchCatalogList as unknown as FetchModelList<IGlossaryTerm>
				}
				withCheckbox
				withInteractiveHeader
				withSearch
				withFilterOptions={filterOptions}
				columns={columns}
				withActions={actions}
				defaultSort={DEFAULT_SORT}
				onCellClick={handleCellClick}
				columnVisibility={COLUMN_VISIBILITY}
				nestingFilter="parent_id"
				defaultRequiredSearchParamsNesting={DEFAULT_SEARCH_PARAMS_NESTING}
				defaultRequiredCatalogFilters={DEFAULT_CATALOG_FILTERS}
				defaultRequiredSearchParams={defaultSearchParams}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
			/>
		</Stack>
	);
}
